import { getBrokerSourceLabel } from "../constants/realEstateFilters";
import { hasMapLocation } from "./brokerListings";
import {
  getAvailabilityBadge,
  getFreshnessBadge,
  getSourceCountLabel,
  getSourceTrustBadge,
} from "./propertyTrustBadges";
import { getPublicSourceTrustLabel } from "./sourceTrustLabels";

const HIDDEN_IMAGE_STATUSES = new Set([
  "BROKEN",
  "INVALID_URL",
  "BROWSER_FAILED",
  "BROKEN_CANDIDATE",
]);

function shouldHideImageUrl(raw = {}) {
  const status = raw.primary_image_health_status || raw.primary_image?.health_status;
  const browserFailures =
    raw.primary_image?.browser_failure_count || raw.primary_image?.browserFailureCount || 0;

  return HIDDEN_IMAGE_STATUSES.has(status) || browserFailures >= 5;
}

export function mapPublicCanonicalProperty(raw = {}) {
  const id = raw.canonical_property_id || raw.canonicalListingId || raw.id;
  const activeSourceCount = raw.active_source_count ?? raw.activeSourceCount ?? 0;
  const sourceCount = raw.source_count ?? raw.sourceCount ?? activeSourceCount;
  const sources = (raw.sources || raw.sourceSummary || []).map((entry) => {
    const mapped = {
      sourceKey: entry.source_key || entry.source || entry.sourceKey,
      displayName:
        entry.display_name ||
        entry.displaySource ||
        getBrokerSourceLabel(entry.source_key || entry.source),
      price: entry.price ?? null,
      sourceUrl: entry.source_url || entry.detailUrl || null,
      lastSeenAt: entry.last_seen_at || entry.lastSeenAt || null,
      trustScore: entry.trust_score ?? entry.trustScore ?? null,
      trustLabel: entry.trust_label || entry.trustLabel || null,
    };
    const publicTrust = getPublicSourceTrustLabel(mapped);

    return {
      ...mapped,
      publicTrustLabel: publicTrust.label,
      publicTrustTone: publicTrust.tone,
    };
  });

  const availabilityStatus = raw.availability_status || raw.availabilityStatus || "AVAILABLE";
  const lastSeenAt = raw.last_seen_at || raw.lastSeenAt || null;

  return {
    id,
    canonicalListingId: id,
    favorited: Boolean(raw.favorited),
    favoriteListingId: raw.favoriteListingId || raw.favorite_listing_id || null,
    isCanonical: true,
    title: raw.title,
    description: raw.description,
    propertyType: raw.property_type || raw.propertyType,
    listingType: raw.listing_type || raw.listingType,
    transactionType: raw.listing_type || raw.listingType,
    priceMin: raw.price_min ?? raw.priceMin,
    priceMax: raw.price_max ?? raw.priceMax,
    currencyCode: raw.currency || raw.currencyCode || "COP",
    bedrooms: raw.bedrooms,
    bathrooms: raw.bathrooms,
    parkingSpaces: raw.parking_spaces ?? raw.parkingSpaces,
    areaM2: raw.area_m2 ?? raw.areaM2 ?? raw.built_area_m2 ?? raw.builtAreaM2,
    builtAreaM2: raw.built_area_m2 ?? raw.builtAreaM2 ?? null,
    neighborhood: raw.neighborhood,
    city: raw.municipality || raw.city,
    municipality: raw.municipality || raw.city,
    latitude: raw.latitude,
    longitude: raw.longitude,
    locationPending: Boolean(raw.location_pending) || !hasMapLocation(raw),
    locationQuality: raw.location_quality || raw.locationQuality || null,
    coordinateSource: raw.coordinate_source || raw.coordinateSource || null,
    approximateLocation: Boolean(raw.approximate_location || raw.approximateLocation),
    coordinateLabel: raw.coordinate_label || raw.coordinateLabel || null,
    imageUrl: shouldHideImageUrl(raw)
      ? null
      : raw.image_url || raw.primary_image_url || raw.primaryImageUrl || null,
    primaryImageUrl: shouldHideImageUrl(raw)
      ? null
      : raw.primary_image_url || raw.image_url || raw.primaryImageUrl || null,
    primaryImageId: raw.primary_image_id || raw.primary_image?.image_id || null,
    primaryImageSourceKey:
      raw.primary_image_source_key || raw.primary_image?.source_key || raw.primary_source_key,
    primaryImageSourceListingId:
      raw.primary_image_source_listing_id || raw.primary_image?.source_listing_id || null,
    primaryImageHealthStatus:
      raw.primary_image_health_status || raw.primary_image?.health_status || null,
    primaryImage: (() => {
      if (shouldHideImageUrl(raw)) {
        return null;
      }

      if (raw.primary_image?.url || raw.primary_image?.image_id) {
        return {
          imageId: raw.primary_image.image_id,
          url: raw.primary_image.url,
          originalUrl:
            raw.primary_image.original_url || raw.primary_original_image_url || null,
          sourceKey: raw.primary_image.source_key,
          sourceListingId: raw.primary_image.source_listing_id,
          canonicalPropertyId: raw.primary_image.canonical_property_id || id,
          healthStatus: raw.primary_image.health_status,
          cacheStatus: raw.primary_image.cache_status || raw.primary_image_cache_status,
          isCached:
            raw.primary_image.is_cached ?? raw.primary_image_is_cached ?? false,
          browserFailureCount: raw.primary_image.browser_failure_count || 0,
          attribution: raw.primary_image.attribution,
        };
      }

      const fallbackUrl =
        raw.image_url || raw.primary_image_url || raw.primaryImageUrl || null;

      if (!fallbackUrl) {
        return null;
      }

      return {
        url: fallbackUrl,
        sourceKey: raw.primary_source_key || raw.primarySourceKey,
        attribution:
          raw.primary_source_name ||
          raw.primarySourceName ||
          getBrokerSourceLabel(raw.primary_source_key || raw.primarySourceKey),
        isCached: raw.primary_image_is_cached ?? false,
        cacheStatus: raw.primary_image_cache_status || null,
      };
    })(),
    primaryImageIsCached: raw.primary_image_is_cached ?? raw.primary_image?.is_cached ?? false,
    primaryImageCacheStatus:
      raw.primary_image_cache_status || raw.primary_image?.cache_status || null,
    primaryOriginalImageUrl:
      raw.primary_original_image_url || raw.primary_image?.original_url || null,
    imageCount: raw.image_count ?? raw.imageCount ?? 0,
    visibleImageCount: raw.visible_image_count ?? raw.visibleImageCount ?? 0,
    gallery: (raw.gallery || []).map((item) => ({
      galleryImageId: item.gallery_image_id || item.galleryImageId,
      imageId: item.image_id || item.imageId,
      url: item.url,
      thumbnailUrl: item.thumbnail_url || item.thumbnailUrl || item.url,
      sourceKey: item.source_key || item.sourceKey,
      sourceName: item.source_name || item.sourceName,
      attribution: item.attribution,
      sourceListingId: item.source_listing_id || item.sourceListingId,
      sourcePageUrl: item.source_page_url || item.sourcePageUrl,
      position: item.position,
      alt: item.alt,
      isPrimary: item.is_primary ?? item.isPrimary,
      isCached: item.is_cached ?? item.isCached,
      cacheStatus: item.cache_status || item.cacheStatus,
    })),
    activeSourceCount,
    sourceCount,
    primarySourceKey: raw.primary_source_key || raw.primarySourceKey,
    primarySourceName:
      raw.primary_source_name ||
      raw.primarySourceName ||
      getBrokerSourceLabel(raw.primary_source_key || raw.primarySourceKey),
    availabilityStatus,
    lastSeenAt,
    lastConfirmedAt: raw.last_confirmed_at || raw.lastConfirmedAt || lastSeenAt,
    sources,
    sourceSummary: sources,
    displaySource: getSourceCountLabel(activeSourceCount),
    availabilityBadge: getAvailabilityBadge(availabilityStatus),
    freshnessBadge: getFreshnessBadge(lastSeenAt),
    sourceTrustBadge: getSourceTrustBadge(activeSourceCount),
    priceHistory: raw.price_history || raw.priceHistory || null,
  };
}

export function mapCanonicalApiListing(raw = {}) {
  const canonicalId =
    raw.canonical_property_id ?? raw.canonicalPropertyId ?? null;

  if (canonicalId != null) {
    return mapPublicCanonicalProperty({
      ...raw,
      canonical_property_id: canonicalId,
    });
  }

  return mapPublicCanonicalProperty({
    canonical_property_id: raw.canonicalListingId || raw.id,
    title: raw.title,
    description: raw.description,
    latitude: raw.latitude,
    longitude: raw.longitude,
    price_min: raw.priceMin,
    price_max: raw.priceMax,
    currency: raw.currency,
    bedrooms: raw.bedrooms,
    bathrooms: raw.bathrooms,
    parking_spaces: raw.parkingSpaces,
    area_m2: raw.areaM2,
    property_type: raw.propertyType,
    listing_type: raw.listingType,
    neighborhood: raw.neighborhood,
    municipality: raw.city,
    availability_status: raw.availabilityStatus,
    active_source_count: raw.activeSourceCount,
    source_count: raw.sourceCount,
    primary_source_key: raw.primarySourceKey,
    last_seen_at: raw.lastSeenAt,
    sources: raw.sourceSummary,
    image_url: raw.primaryImageUrl,
    image_count: raw.imageCount,
    visible_image_count: raw.visibleImageCount,
    gallery: raw.gallery,
    primary_image: raw.primaryImage,
    price_history: raw.priceHistory,
    location_pending: raw.latitude == null || raw.longitude == null,
  });
}
